// src/app/_util/tag-scope.util.ts
// tasks/frontend-tagging.md (Phase 2): the two tag scopes the backend knows (Phase 1).
//
// - SPACE tags belong to exactly one Space and are assigned through the shared Note's own `Tags`
//   field (normal Note save/sync).
// - PRIVATE tags belong to the authenticated user and are assigned through the separate
//   private-tag-assignment API — never through `Note.Tags`.
//
// Only these two scopes are current. Every pre-Phase-1 tag was migrated to PRIVATE (with its owner),
// so a tag WITHOUT a valid `scope` is obsolete data: tag catalogs drop it at the source (see
// hasCurrentTagScope() and DataService.getAllTags()) and it is never offered, shown or assigned.

import type { Tag } from '../_service/data.service';

export const TAG_SCOPE = {
  SPACE: 'SPACE',
  PRIVATE: 'PRIVATE',
} as const;

export type TagScope = (typeof TAG_SCOPE)[keyof typeof TAG_SCOPE];

/** True only for a current tag: scope SPACE (with its Space) or PRIVATE. Anything else is obsolete. */
export function hasCurrentTagScope(tag: Pick<Tag, 'scope' | 'spaceId'> | null | undefined): boolean {
  if (tag?.scope === TAG_SCOPE.PRIVATE) return true;
  return tag?.scope === TAG_SCOPE.SPACE && !!tag.spaceId;
}

/** Drops obsolete (unscoped) tags from a catalog. */
export function filterCurrentTags<T extends Pick<Tag, 'scope' | 'spaceId'>>(tags: T[]): T[] {
  return (tags ?? []).filter((tag) => hasCurrentTagScope(tag));
}

/** Scope of a current tag. Catalogs only ever hold current tags (filterCurrentTags), so the
 * non-SPACE branch is PRIVATE. */
export function getTagScope(tag: Pick<Tag, 'scope'> | null | undefined): TagScope {
  return tag?.scope === TAG_SCOPE.SPACE ? TAG_SCOPE.SPACE : TAG_SCOPE.PRIVATE;
}

export function isPrivateTag(tag: Pick<Tag, 'scope'> | null | undefined): boolean {
  return tag?.scope === TAG_SCOPE.PRIVATE;
}

/** The tag types offered when creating/editing a tag (note editor and Tag Manager alike). */
export const TAG_TYPE_OPTIONS: readonly string[] = [
  'calendar',
  'concept',
  'location',
  'organization',
  'person',
  'project',
  'topic',
  'technology',
];
export const DEFAULT_TAG_TYPE = 'topic';

/** Tag type that marks calendar notes (Calendar view) — matched on the tag's type, never its id/name. */
export const CALENDAR_TAG_TYPE = 'calendar';

export function isCalendarTag(tag: Pick<Tag, 'tagType'> | null | undefined): boolean {
  return String(tag?.tagType ?? '').trim().toLowerCase() === CALENDAR_TAG_TYPE;
}

/** SPACE tags of exactly `spaceId` — never another Space's. */
export function filterSpaceTags(tags: Tag[], spaceId: string | null | undefined): Tag[] {
  if (!spaceId) return [];
  return tags.filter((tag) => getTagScope(tag) === TAG_SCOPE.SPACE && tag.spaceId === spaceId);
}

export function filterPrivateTags(tags: Tag[]): Tag[] {
  return tags.filter((tag) => isPrivateTag(tag));
}

/**
 * The Space tags a note carries in `Note.Tags` — tag ids of the note's own Space only. Ids are
 * resolved against `spaceCatalog`; an id not in it yet (catalog still loading/offline, or a tag a
 * member just created) is kept as an "(unresolved tag)" placeholder so a save never drops the
 * reference. Entries that aren't Space tag ids (obsolete pre-migration data: plain names, embedded
 * objects, the user's own private tag ids) are left out — the server strips them on save anyway.
 */
export function resolveNoteSpaceTags(
  rawTags: unknown,
  spaceCatalog: Tag[],
  options: { privateTagIds?: Iterable<string>; spaceId?: string | null } = {},
): Tag[] {
  if (!Array.isArray(rawTags)) return [];
  const byId = new Map(spaceCatalog.map((tag) => [tag._id, tag] as const));
  const notSpace = new Set(options.privateTagIds ?? []);
  const seen = new Set<string>();
  const result: Tag[] = [];
  for (const raw of rawTags) {
    const id = (typeof raw === 'string' ? raw : String((raw as Tag | null)?._id ?? '')).trim();
    if (!id || seen.has(id) || notSpace.has(id)) continue;
    seen.add(id);
    const known = byId.get(id);
    if (known) {
      result.push(known);
    } else if (id.startsWith('tag:')) {
      result.push({
        _id: id,
        type: 'tag',
        name: '(unresolved tag)',
        tagType: '',
        slug: id,
        color: '#94a3b8',
        parentTagId: null,
        isActive: true,
        scope: TAG_SCOPE.SPACE,
        spaceId: options.spaceId ?? null,
      });
    }
  }
  return result;
}

/** Picker/autocomplete groups — Space tags first, then Private. Empty groups are omitted. */
export interface TagSuggestionGroup {
  label: string;
  scope: TagScope;
  items: Tag[];
}

export function groupTagsByScope(spaceTags: Tag[], privateTags: Tag[]): TagSuggestionGroup[] {
  const groups: TagSuggestionGroup[] = [];
  if (spaceTags.length) groups.push({ label: 'Space tags', scope: TAG_SCOPE.SPACE, items: spaceTags });
  if (privateTags.length) groups.push({ label: 'Private tags', scope: TAG_SCOPE.PRIVATE, items: privateTags });
  return groups;
}

// ---------------------------------------------------------------------------------------------
// tasks/frontend-tagging-phase3.md — Home tag-filter scope.
// ---------------------------------------------------------------------------------------------

/** Which tags a Home tag filter matches against. ANY is the default (also for a missing value). */
export const TAG_FILTER_SCOPE = {
  ANY: 'ANY',
  SPACE: 'SPACE',
  PRIVATE: 'PRIVATE',
} as const;

export type TagFilterScope = (typeof TAG_FILTER_SCOPE)[keyof typeof TAG_FILTER_SCOPE];

/** Missing/unknown → ANY, so filters saved before tag scopes existed keep working. */
export function normalizeTagFilterScope(value: unknown): TagFilterScope {
  return value === TAG_FILTER_SCOPE.SPACE || value === TAG_FILTER_SCOPE.PRIVATE ? value : TAG_FILTER_SCOPE.ANY;
}
